import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const inputSources = [
  { label: '视频', icon: 'ri-video-line', color: 'primary' },
  { label: '音频', icon: 'ri-mic-line', color: 'accent' },
  { label: 'ASR文本', icon: 'ri-file-text-line', color: 'primary' },
  { label: '课件', icon: 'ri-slideshow-3-line', color: 'accent' },
  { label: '教材章节', icon: 'ri-book-open-line', color: 'primary' },
  { label: '评价指标', icon: 'ri-list-check-3', color: 'accent' },
];

const sliceHighlights = [
  { time: '02:15', label: '导入', desc: '提问引入，现实场景切入', color: 'bg-accent-100/50 text-accent-700 border-accent-300/40' },
  { time: '08:40', label: '案例进入', desc: '史料呈现，问题展开', color: 'bg-primary-100/50 text-primary-700 border-primary-300/40' },
  { time: '18:30', label: '提问', desc: '开放问题，学生回应', color: 'bg-accent-100/50 text-accent-700 border-accent-300/40' },
  { time: '25:15', label: '互动', desc: '追问深入，回应进入理论', color: 'bg-primary-100/50 text-primary-700 border-primary-300/40' },
  { time: '38:00', label: '总结升华', desc: '价值回扣，教学收束', color: 'bg-accent-100/50 text-accent-700 border-accent-300/40' },
];

export default function P26ClassroomInputNew({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-3 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            课堂实录与有效切片
          </span>
          <h2 className={`font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从长视频中<span className="text-primary-600">保留教学动作</span>
          </h2>
        </div>

        {/* 两栏布局 */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* 左侧：输入漏斗 */}
          <div>
            <h3 className="font-heading text-sm font-bold text-foreground-800 mb-3 flex items-center gap-2">
              <i className="ri-upload-cloud-2-line text-primary-500" />
              多源材料输入
            </h3>
            <div className="grid grid-cols-3 gap-2.5">
              {inputSources.map((src) => (
                <div key={src.label} className={`p-3 rounded-xl text-center transition-all duration-300 hover:scale-[1.03] ${src.color === 'primary' ? 'glass-blue border-primary-200/30' : 'glass-gold border-accent-200/30'}`}>
                  <i className={`${src.icon} text-lg ${src.color === 'primary' ? 'text-primary-500' : 'text-accent-500'}`} />
                  <p className="text-[11px] font-body font-semibold text-foreground-700 mt-1">{src.label}</p>
                </div>
              ))}
            </div>

            {/* 对齐说明 */}
            <div className="mt-4 p-4 rounded-2xl glass border-white/50">
              <p className="text-xs text-foreground-600 font-body leading-relaxed">
                <i className="ri-timer-line text-primary-500 mr-1" />
                多种材料在<strong className="text-primary-700">同一时间轴</strong>上对齐：教师说到某个概念时，系统同时知道屏幕展示了什么、声音节奏发生了什么变化、前后有没有提问和回应。
              </p>
            </div>
          </div>

          {/* 右侧：45分钟时间轴 */}
          <div>
            <h3 className="font-heading text-sm font-bold text-foreground-800 mb-3 flex items-center gap-2">
              <i className="ri-scissors-line text-accent-500" />
              有效切片：从45分钟中提取关键教学动作
            </h3>
            <div className="p-4 rounded-2xl glass-strong border-primary-200/30">
              {/* 时间轴 */}
              <div className="relative">
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-background-200 rounded-full" />
                {sliceHighlights.map((slice, i) => (
                  <div key={slice.time} className="relative flex items-start gap-4 mb-3 last:mb-0">
                    <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${slice.color.split(' ')[0]} border ${slice.color.split(' ')[2] || 'border-primary-300/40'}`}>
                      <div className="w-2 h-2 rounded-full bg-current" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[11px] text-foreground-400">{slice.time}</span>
                        <span className="text-xs font-body font-bold text-foreground-700">{slice.label}</span>
                      </div>
                      <p className="text-[11px] text-foreground-500 font-body mt-0.5">{slice.desc}</p>
                    </div>
                  </div>
                ))}
                {/* 淡化区域标记 */}
                <div className="absolute right-2 top-3 bottom-3 w-1.5 rounded-full bg-background-200/60">
                  {[0, 1, 2, 3, 4].map((n) => (
                    <div key={n} className="absolute left-0 w-full h-8 rounded-full bg-background-100/60" style={{ top: `${n * 22}%` }} />
                  ))}
                </div>
              </div>
            </div>

            {/* 工程说明 */}
            <div className="mt-3 px-4 py-2.5 rounded-xl glass-gold border-accent-200/30">
              <p className="text-[11px] text-foreground-500 font-body leading-relaxed">
                <i className="ri-flashlight-line text-accent-500 mr-1" />
                关键帧采用4帧采样即可接近全帧处理效果，可在减少约一半计算量的同时保留主要课堂结构信息。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}